import { useCallback, useEffect, useState } from "react";
import { Form, Input, Switch } from "antd";
import toast from "react-hot-toast";
import Edit from "../../../assets/Edit.jsx";
import LoadableButton from "../../../components/buttons/LoadableButton.jsx";
import ConfirmationModal from "../../../components/global/modal/ConfirmationModal.jsx";
import ModalComponent from "../../../components/global/modal/ModalComponent.jsx";
import TableWithSkeleton from "../../../components/global/table/TableWithSkeleton.jsx";
import {
  addUpdateSalesOffice,
  deleteSalesOffice,
  getSalesOffices,
} from "../../../api/request/settings/salesOffice.js";

export default function SalesOffice() {
  const [form] = Form.useForm();
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [officeModalOpen, setOfficeModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [editingRecord, setEditingRecord] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [statusPendingId, setStatusPendingId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchOffices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSalesOffices({ Alldata: true });
      if (res?.data?.success) {
        setOffices(res.data.data || []);
      } else {
        toast.error(res?.data?.message || "Failed to load sales offices.");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          "Failed to load sales offices.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOffices();
  }, [fetchOffices]);

  const handleStatusChange = useCallback(
    async (record, checked) => {
      setStatusPendingId(record.id);
      try {
        const { data } = await addUpdateSalesOffice({
          id: record.id,
          officeName: record.officeName,
          code: record.code,
          status: checked,
        });
        if (data?.success) {
          toast.success(data?.message);
          await fetchOffices();
        } else {
          toast.error(data?.message);
          await fetchOffices();
        }
      } catch (error) {
        toast.error(
          error?.response?.data?.error?.message ||
            error?.response?.data?.message ||
            "Failed to update office status.",
        );
        await fetchOffices();
      } finally {
        setStatusPendingId(null);
      }
    },
    [fetchOffices],
  );

  const openAddModal = useCallback(() => {
    setModalMode("add");
    setEditingRecord(null);
    form.resetFields();
    setOfficeModalOpen(true);
  }, [form]);

  const openEditModal = useCallback(
    (record) => {
      setModalMode("edit");
      setEditingRecord(record);
      form.setFieldsValue({
        officeName: record.officeName,
        code: record.code,
      });
      setOfficeModalOpen(true);
    },
    [form],
  );

  const openDeleteModal = useCallback((record) => {
    setDeleteTarget(record);
    setDeleteOpen(true);
  }, []);

  const handleOfficeModalOpenChange = useCallback(
    (open) => {
      if (!open) {
        form.resetFields();
        setEditingRecord(null);
      }
      setOfficeModalOpen(open);
    },
    [form],
  );

  const columns = [
    {
      title: "Office Name",
      dataIndex: "officeName",
      key: "officeName",
      render: (text) => text || "—",
    },
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      render: (text) => text || "—",
    },
    {
      title: "Status",
      key: "status",
      width: 130,
      render: (_, record) => (
        <Switch
          checked={Boolean(record.status)}
          disabled={statusPendingId === record.id}
          checkedChildren="On"
          unCheckedChildren="Off"
          onChange={(checked) => handleStatusChange(record, checked)}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="p-0 border-0 bg-transparent cursor-pointer"
            aria-label="Edit office"
            onClick={() => openEditModal(record)}
          >
            <Edit />
          </button>
          <button
            type="button"
            className="p-0 border-0 bg-transparent cursor-pointer"
            aria-label="Delete office"
            onClick={() => openDeleteModal(record)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="#D91819"
              className="w-6 h-6 cursor-pointer hover:stroke-[#aa0001] duration-500"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
              />
            </svg>
          </button>
        </div>
      ),
    },
  ];

  const handleFormFinish = async (values) => {
    setSubmitLoading(true);
    try {
      const payload =
        modalMode === "add"
          ? {
              officeName: values.officeName?.trim(),
              code: values.code?.trim(),
            }
          : (() => {
              const row = offices.find((o) => o.id === editingRecord.id);
              return {
                id: editingRecord.id,
                officeName: values.officeName?.trim(),
                code: values.code?.trim(),
                status: Boolean(row?.status ?? editingRecord.status),
              };
            })();
      const { data } = await addUpdateSalesOffice(payload);
      if (data?.success) {
        toast.success(data?.message);
        handleOfficeModalOpenChange(false);
        await fetchOffices();
      } else {
        toast.error(data?.message);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          "Failed to save sales office.",
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    setDeleteLoading(true);
    try {
      const { data } = await deleteSalesOffice(deleteTarget.id);
      if (data?.success) {
        toast.success(data?.message);
        setDeleteOpen(false);
        setDeleteTarget(null);
        await fetchOffices();
      } else {
        toast.error(data?.message);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          "Failed to delete sales office.",
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-end">
        <button
          type="button"
          className="export-btm flex bg-primary text-sm text-white font-medium uppercase px-9 py-1.5 rounded-lg themeHover duration-400"
          onClick={openAddModal}
        >
          <img
            src="assets/icons/add_icon.svg"
            alt=""
            className="mr-2 mt-1"
            width={15}
          />
          Add Office
        </button>
      </div>

      <div className="border-[2px] border-[var(--theme-light)] rounded-xl pt-4 lg:pt-6 w-full mt-5">
        <div className="px-4 lg:px-6">
          <p className="text-xl font-semibold">Sales Offices</p>
        </div>
        <div className="my-6">
          <TableWithSkeleton
            rowKey="id"
            columns={columns}
            data={offices}
            loading={loading}
          />
        </div>
      </div>

      <ModalComponent
        isOpen={officeModalOpen}
        setIsOpen={handleOfficeModalOpenChange}
        title={
          <div className="font-bold text-lg">
            {modalMode === "add" ? "Add Sales Office" : "Edit Sales Office"}
          </div>
        }
        width={440}
      >
        <Form
          form={form}
          layout="vertical"
          autoComplete="off"
          onFinish={handleFormFinish}
        >
          <Form.Item
            name="officeName"
            label="Office Name"
            rules={[{ required: true, message: "Please enter office name." }]}
          >
            <Input placeholder="Office name" className="h-11" />
          </Form.Item>
          <Form.Item
            name="code"
            label="Code"
            rules={[{ required: true, message: "Please enter office code." }]}
          >
            <Input placeholder="Office code" className="h-11" />
          </Form.Item>
          <div className="flex justify-end gap-2 mt-4">
            <LoadableButton
              type="button"
              className="!bg-gray-200 !text-gray-800 uppercase"
              lable="Cancel"
              onClick={() => handleOfficeModalOpenChange(false)}
              isConfirmBtn
            />
            <LoadableButton
              type="Submit"
              className="bg-primary text-sm text-white font-semibold rounded-lg px-8 py-1.5 uppercase themeHover duration-500"
              lable={modalMode === "add" ? "Save" : "Update"}
              isLoading={submitLoading}
              disabled={submitLoading}
              loadingLable={modalMode === "add" ? "Saving..." : "Updating..."}
            />
          </div>
        </Form>
      </ModalComponent>

      <ConfirmationModal
        isOpen={deleteOpen}
        setIsOpen={setDeleteOpen}
        message="Are you sure you want to delete this sales office?"
        onConfirm={handleDeleteConfirm}
        isLoading={deleteLoading}
        loadingLabel="Deleting..."
      />
    </div>
  );
}
